import * as React from "react";
import { ArrowRight, FileDown, ReceiptText, WalletCards } from "lucide-react";
import type { AnnualTaxAdjustment, AnnualTaxSettings, AppData, AppView, AuthUser, DashboardMode, DocumentScanDraft, Expense, IncomeSummary } from "@/types/tax";
import { AppShell } from "@/components/common/AppShell";
import { PwaPrompts } from "@/components/common/PwaPrompts";
import { AnnualTaxPanel } from "@/components/domain/AnnualTaxPanel";
import { AuthScreen } from "@/components/domain/AuthScreen";
import { BasSummaryCard } from "@/components/domain/BasSummaryCard";
import { DocumentsTable } from "@/components/domain/DocumentsTable";
import { ExpenseForm } from "@/components/domain/ExpenseForm";
import { ExpenseTable } from "@/components/domain/ExpenseTable";
import { IncomeForm } from "@/components/domain/IncomeForm";
import { IncomeTable } from "@/components/domain/IncomeTable";
import { MonthlyGstTable } from "@/components/domain/MonthlyGstTable";
import { PdfScanPanel } from "@/components/domain/PdfScanPanel";
import { SettingsPanel } from "@/components/domain/SettingsPanel";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Dropdown } from "@/components/ui/Dropdown";
import { emptyAppData } from "@/data/defaults";
import {
  AUTH_REQUIRED_EVENT,
  deleteExpense as apiDeleteExpense,
  deleteIncomeSummary as apiDeleteIncomeSummary,
  fetchAppData,
  getCurrentUser,
  login,
  logout,
  register,
  removeDocumentPdf as apiRemoveDocumentPdf,
  saveExpense as apiSaveExpense,
  saveIncomeSummary as apiSaveIncomeSummary,
  saveAnnualTaxAdjustment as apiSaveAnnualTaxAdjustment,
  saveAnnualTaxSettings as apiSaveAnnualTaxSettings,
  saveProfile as apiSaveProfile,
  deleteAnnualTaxAdjustment as apiDeleteAnnualTaxAdjustment,
} from "@/api/client";
import { availableBasPeriods, availableFinancialYears, defaultAnnualTaxSettings, getBasPeriodForDate, monthlyFinancialSummaries, summarizeAnnualTax, summarizeBas } from "@/lib/gst";
import { cn } from "@/lib/utils";

type AppNotice = {
  id: number;
  variant: "success" | "danger" | "info";
  title: string;
  message: string;
};

type ConfirmAction = {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
};

const appViews: AppView[] = ["dashboard", "income", "expenses", "documents", "settings"];

function viewFromUrl(): AppView {
  if (typeof window === "undefined") return "dashboard";
  const url = new URL(window.location.href);
  const view = url.searchParams.get("view") as AppView | null;
  if (!view && ["#gst-settings", "#notification-settings", "#billing-settings", "#install-settings"].includes(url.hash)) {
    return "settings";
  }
  return view && appViews.includes(view) ? view : "dashboard";
}

export default function App() {
  const [authUser, setAuthUser] = React.useState<AuthUser | null>(null);
  const [authError, setAuthError] = React.useState("");
  const [loadingAuth, setLoadingAuth] = React.useState(true);
  const [appData, setAppData] = React.useState<AppData>(emptyAppData);
  const [activeView, setActiveView] = React.useState<AppView>(() => viewFromUrl());
  const [dashboardMode, setDashboardMode] = React.useState<DashboardMode>("bas");
  const [notice, setNotice] = React.useState<AppNotice | null>(null);
  const [incomeDraft, setIncomeDraft] = React.useState<DocumentScanDraft | null>(null);
  const [expenseDraft, setExpenseDraft] = React.useState<DocumentScanDraft | null>(null);
  const [editingIncome, setEditingIncome] = React.useState<IncomeSummary | null>(null);
  const [editingExpense, setEditingExpense] = React.useState<Expense | null>(null);
  const [incomeMobilePanel, setIncomeMobilePanel] = React.useState<"manual" | "scan">("scan");
  const [expenseMobilePanel, setExpenseMobilePanel] = React.useState<"manual" | "scan">("scan");
  const [confirmAction, setConfirmAction] = React.useState<ConfirmAction | null>(null);
  const [showSettingsOnboarding, setShowSettingsOnboarding] = React.useState(false);
  const incomeFormRef = React.useRef<HTMLDivElement>(null);
  const expenseFormRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    getCurrentUser().then((result) => {
      if (result.ok && result.data) {
        setAuthUser(result.data);
        fetchAppData().then((dataResult) => {
          if (dataResult.ok) {
            setAppData(dataResult.data);
          } else {
            setAuthError(dataResult.error);
            setAuthUser(null);
          }
          setLoadingAuth(false);
        });
      } else {
        setLoadingAuth(false);
      }
    });
  }, []);

  React.useEffect(() => {
    function onAuthRequired() {
      setAuthUser(null);
      setAppData(emptyAppData);
      setConfirmAction(null);
      setAuthError("Your session has expired. Please sign in again.");
      navigate("dashboard");
    }

    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  React.useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => {
      setNotice((current) => (current?.id === notice.id ? null : current));
    }, 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  React.useEffect(() => {
    const onPopState = () => setActiveView(viewFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function navigate(view: AppView) {
    setActiveView(view);
    if (view === "settings") setShowSettingsOnboarding(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("view");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }

  React.useEffect(() => {
    if (!authUser) return undefined;

    let lastRefresh = 0;
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRefresh < 4000) return;
      lastRefresh = now;
      void fetchAppData().then((result) => {
        if (result.ok) setAppData(result.data);
      });
    };

    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("pageshow", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("pageshow", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
    };
  }, [authUser]);

  React.useEffect(() => {
    if (!authUser || activeView !== "documents") return;
    void fetchAppData().then((result) => {
      if (result.ok) setAppData(result.data);
    });
  }, [activeView, authUser]);

  function notify(variant: AppNotice["variant"], title: string, message: string) {
    setNotice({ id: Date.now(), variant, title, message });
  }

  function scrollToForm(ref: React.RefObject<HTMLElement>, options: { focus?: boolean } = {}) {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (options.focus === false) return;
      const focusTarget = ref.current?.querySelector<HTMLElement>("input, button, textarea");
      focusTarget?.focus({ preventScroll: true });
    }, 0);
  }

  async function handleLogin(email: string, password: string, displayName?: string) {
    setAuthError("");
    setLoadingAuth(true);
    const result = displayName ? await register(email, password, displayName) : await login(email, password);
    if (result.ok) {
      setAuthUser(result.data);
      const dataResult = await fetchAppData();
      if (dataResult.ok) {
        setAppData(dataResult.data);
        if (displayName) setShowSettingsOnboarding(true);
      } else {
        setAuthError(dataResult.error);
        setAuthUser(null);
      }
    } else {
      setAuthError(result.error);
    }
    setLoadingAuth(false);
  }

  async function handleLogout() {
    await logout();
    setAuthUser(null);
    setAppData(emptyAppData);
    navigate("dashboard");
  }

  function updateData(next: AppData) {
    setAppData(next);
  }

  function removeDocumentPdf(id: string) {
    const document = appData.documents.find((item) => item.id === id);
    setConfirmAction({
      title: "Delete source file?",
      description: `Delete source file${document?.fileName ? ` "${document.fileName}"` : ""}? Accepted income and expense records will stay saved.`,
      confirmLabel: "Delete file",
      onConfirm: () => void confirmRemoveDocumentPdf(id),
    });
  }

  async function confirmRemoveDocumentPdf(id: string) {
    setConfirmAction(null);
    updateData({
      ...appData,
      documents: appData.documents.filter((document) => document.id !== id),
    });

    const result = await apiRemoveDocumentPdf(id);
    if (result.ok) {
      updateData({ ...appData, documents: result.data });
      notify("success", "Source file deleted", "Accepted income and expense records are still saved.");
    } else {
      notify("danger", "Could not delete PDF", result.error);
    }
  }

  function saveIncome(income: IncomeSummary) {
    setIncomeDraft(null);
    setEditingIncome(null);
    const existing = appData.incomeSummaries.some((item) => item.id === income.id);
    updateData({
      ...appData,
      incomeSummaries: existing
        ? appData.incomeSummaries.map((item) => (item.id === income.id ? income : item))
        : [income, ...appData.incomeSummaries],
    });
    void apiSaveIncomeSummary(income).then((result) => {
      if (!result.ok) {
        notify("danger", "Income sync failed", result.error);
      } else {
        notify("success", existing ? "Income updated" : "Income saved", "Monthly GST figures have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  function deleteIncome(id: string) {
    setConfirmAction({
      title: "Delete income record?",
      description: "This income record will be removed and monthly GST figures will update.",
      confirmLabel: "Delete income",
      onConfirm: () => confirmDeleteIncome(id),
    });
  }

  function confirmDeleteIncome(id: string) {
    setConfirmAction(null);
    if (editingIncome?.id === id) setEditingIncome(null);
    updateData({ ...appData, incomeSummaries: appData.incomeSummaries.filter((income) => income.id !== id) });
    void apiDeleteIncomeSummary(id).then((result) => {
      if (!result.ok) {
        notify("danger", "Could not delete income", result.error);
      } else {
        notify("success", "Income deleted", "Monthly GST figures have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  function saveExpense(expense: Expense) {
    setExpenseDraft(null);
    setEditingExpense(null);
    const normalizedExpense = { ...expense, entrySource: expense.entrySource || "manual" };
    const existing = appData.expenses.some((item) => item.id === expense.id);
    updateData({
      ...appData,
      expenses: existing
        ? appData.expenses.map((item) => (item.id === expense.id ? normalizedExpense : item))
        : [normalizedExpense, ...appData.expenses],
    });
    void apiSaveExpense(normalizedExpense).then((result) => {
      if (!result.ok) {
        notify("danger", "Expense sync failed", result.error);
      } else {
        notify("success", existing ? "Expense updated" : "Expense saved", "GST credits have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  function deleteExpense(id: string) {
    setConfirmAction({
      title: "Delete expense record?",
      description: "This expense record will be removed and GST credits will update.",
      confirmLabel: "Delete expense",
      onConfirm: () => confirmDeleteExpense(id),
    });
  }

  function confirmDeleteExpense(id: string) {
    setConfirmAction(null);
    if (editingExpense?.id === id) setEditingExpense(null);
    updateData({ ...appData, expenses: appData.expenses.filter((expense) => expense.id !== id) });
    void apiDeleteExpense(id).then((result) => {
      if (!result.ok) {
        notify("danger", "Could not delete expense", result.error);
      } else {
        notify("success", "Expense deleted", "GST credits have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  function editIncome(income: IncomeSummary) {
    setIncomeDraft(null);
    setEditingIncome(income);
    setIncomeMobilePanel("manual");
    navigate("income");
    scrollToForm(incomeFormRef);
  }

  function editExpense(expense: Expense) {
    setExpenseDraft(null);
    setEditingExpense(expense);
    setExpenseMobilePanel("manual");
    setSelectedPeriodId(getBasPeriodForDate(expense.date).id);
    navigate("expenses");
    scrollToForm(expenseFormRef);
  }

  async function saveSettings(profile: AppData["profile"]) {
    updateData({ ...appData, profile });

    const result = await apiSaveProfile(profile);
    if (result.ok) {
      updateData({ ...appData, profile: result.data });
      notify("success", "Settings saved", "Your GST settings were saved to your account.");
    } else {
      notify("danger", "Settings sync failed", result.error);
    }
  }

  async function saveAnnualSettings(settings: AnnualTaxSettings) {
    const existing = appData.annualTaxSettings.some((item) => item.id === settings.id || item.financialYear === settings.financialYear);
    const nextSettings = existing
      ? appData.annualTaxSettings.map((item) => (item.id === settings.id || item.financialYear === settings.financialYear ? settings : item))
      : [settings, ...appData.annualTaxSettings];
    updateData({ ...appData, annualTaxSettings: nextSettings });
    const result = await apiSaveAnnualTaxSettings(settings);
    if (!result.ok) {
      notify("danger", "Annual settings sync failed", result.error);
      return;
    }
    notify("success", "Annual settings saved", "Vehicle method settings were saved.");
    const dataResult = await fetchAppData();
    if (dataResult.ok) updateData(dataResult.data);
  }

  function saveAnnualAdjustment(adjustment: AnnualTaxAdjustment) {
    setAppData((current) => {
      const existing = current.annualTaxAdjustments.some((item) => item.id === adjustment.id);
      return {
        ...current,
        annualTaxAdjustments: existing
          ? current.annualTaxAdjustments.map((item) => (item.id === adjustment.id ? adjustment : item))
          : [adjustment, ...current.annualTaxAdjustments],
      };
    });
    void apiSaveAnnualTaxAdjustment(adjustment).then((result) => {
      if (!result.ok) {
        notify("danger", "Annual adjustment sync failed", result.error);
      } else {
        const savedAdjustment = result.data;
        setAppData((current) => {
          const existing = current.annualTaxAdjustments.some((item) => item.id === savedAdjustment.id);
          return {
            ...current,
            annualTaxAdjustments: existing
              ? current.annualTaxAdjustments.map((item) => (item.id === savedAdjustment.id ? savedAdjustment : item))
              : [savedAdjustment, ...current.annualTaxAdjustments],
          };
        });
        notify("success", "Annual adjustment saved", "Annual tax worksheet figures have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  function deleteAnnualAdjustment(id: string) {
    const adjustment = appData.annualTaxAdjustments.find((item) => item.id === id);
    setConfirmAction({
      title: "Delete annual adjustment?",
      description: `Delete annual adjustment${adjustment?.description ? ` "${adjustment.description}"` : ""}? Annual tax worksheet figures will update.`,
      confirmLabel: "Delete adjustment",
      onConfirm: () => confirmDeleteAnnualAdjustment(id),
    });
  }

  function confirmDeleteAnnualAdjustment(id: string) {
    setConfirmAction(null);
    updateData({ ...appData, annualTaxAdjustments: appData.annualTaxAdjustments.filter((adjustment) => adjustment.id !== id) });
    void apiDeleteAnnualTaxAdjustment(id).then((result) => {
      if (!result.ok) {
        notify("danger", "Could not delete adjustment", result.error);
      } else {
        notify("success", "Annual adjustment deleted", "Annual tax worksheet figures have been updated.");
        void fetchAppData().then((dataResult) => {
          if (dataResult.ok) updateData(dataResult.data);
        });
      }
    });
  }

  const periods = availableBasPeriods(appData.incomeSummaries, appData.expenses);
  const [selectedPeriodId, setSelectedPeriodId] = React.useState(periods[0]?.id || getBasPeriodForDate(new Date()).id);
  const selectedPeriod = periods.find((period) => period.id === selectedPeriodId) || periods[0] || getBasPeriodForDate(new Date());
  const summary = summarizeBas(selectedPeriod, appData.incomeSummaries, appData.expenses, appData.documents);
  const monthlyRows = monthlyFinancialSummaries(summary.incomeSummaries, summary.expenses);
  const financialYears = availableFinancialYears(appData.incomeSummaries, appData.expenses);
  const [selectedFinancialYearId, setSelectedFinancialYearId] = React.useState(financialYears[0]?.id || "");
  const selectedFinancialYear = financialYears.find((year) => year.id === selectedFinancialYearId) || financialYears[0];
  const annualSettings = appData.annualTaxSettings.find((settings) => settings.financialYear === selectedFinancialYear.financialYear)
    || defaultAnnualTaxSettings(authUser?.id || appData.profile.id || "user", selectedFinancialYear.financialYear);
  const annualSummary = summarizeAnnualTax(selectedFinancialYear, appData.incomeSummaries, appData.expenses, annualSettings, appData.annualTaxAdjustments);
  const userPlan = appData.profile.plan || "free";
  const showPeriodSelector = activeView === "income" || activeView === "expenses";

  React.useEffect(() => {
    if (!periods.some((period) => period.id === selectedPeriodId) && periods[0]) {
      setSelectedPeriodId(periods[0].id);
    }
  }, [periods, selectedPeriodId]);

  React.useEffect(() => {
    if (!financialYears.some((year) => year.id === selectedFinancialYearId) && financialYears[0]) {
      setSelectedFinancialYearId(financialYears[0].id);
    }
  }, [financialYears, selectedFinancialYearId]);

  if (!authUser) {
    return (
      <>
        <AuthScreen onLogin={handleLogin} error={authError} loading={loadingAuth} />
        <PwaPrompts autoInstallGuide={false} />
      </>
    );
  }

  const title = {
    dashboard: "Dashboard",
    income: "Income",
    expenses: "Expenses",
    documents: "Documents",
    settings: "Settings",
  }[activeView];

  return (
    <AppShell
      activeView={activeView}
      onNavigate={navigate}
      profile={appData.profile}
      onLogout={handleLogout}
    >
      <PwaPrompts />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-primary">GST/BAS assistance</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-ink">{title}</h1>
        </div>
        {showPeriodSelector ? (
          <div className="flex flex-wrap gap-2">
            <Dropdown
              label="Reporting period"
              value={selectedPeriod.id}
              options={periods.map((period) => ({ value: period.id, label: period.label, description: `${period.startDate} to ${period.endDate}` }))}
              onChange={setSelectedPeriodId}
              className="min-w-56"
            />
          </div>
      ) : null}
      </div>

      {showSettingsOnboarding && activeView !== "settings" ? (
        <SettingsOnboardingPrompt
          onOpenSettings={() => navigate("settings")}
          onDismiss={() => setShowSettingsOnboarding(false)}
        />
      ) : null}

      {activeView !== "dashboard" && activeView !== "settings" ? (
        <Alert className="mb-5 py-2 text-xs sm:text-sm">
          BAS worksheet figures only. Review before lodging.
        </Alert>
      ) : null}

      {notice ? (
        <div className="fixed right-4 top-4 z-50 w-[calc(100%-2rem)] max-w-sm sm:right-6 sm:top-6" role="status" aria-live="polite">
          <Alert variant={notice.variant} title={notice.title} className="bg-white shadow-panel">
            {notice.message}
          </Alert>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.title || ""}
        description={confirmAction?.description || ""}
        confirmLabel={confirmAction?.confirmLabel || "Delete"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm()}
      />

      {activeView === "dashboard" ? (
        <div className="flex flex-col gap-5">
          <DashboardModeSwitcher value={dashboardMode} onChange={setDashboardMode} />
          {dashboardMode === "bas" ? (
            <div className="sm:w-fit">
              <Dropdown
                label="Reporting period"
                value={selectedPeriod.id}
                options={periods.map((period) => ({ value: period.id, label: period.label, description: `${period.startDate} to ${period.endDate}` }))}
                onChange={setSelectedPeriodId}
                className="min-w-0 sm:min-w-56"
              />
            </div>
          ) : (
            <div className="grid grid-cols-[minmax(0,1fr)_9rem] gap-2 sm:flex sm:w-fit sm:flex-row">
              <Dropdown
                label="Financial year"
                value={selectedFinancialYear.id}
                options={financialYears.map((year) => ({ value: year.id, label: year.label, description: `${year.startDate} to ${year.endDate}` }))}
                onChange={setSelectedFinancialYearId}
                className="min-w-0 sm:min-w-56"
              />
              <Button type="button" variant="secondary" className="px-3 sm:px-4" asChild>
                <a href={`api/export.php?kind=annual-tax&year=${encodeURIComponent(annualSummary.period.financialYear)}`}>
                  <FileDown className="size-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Export CSV</span>
                  <span className="sm:hidden">Export</span>
                </a>
              </Button>
            </div>
          )}
          {dashboardMode === "bas" ? (
            <>
              <BasSummaryCard summary={summary} />
              <Alert title="BAS assistance only" className="py-2 text-xs sm:py-3 sm:text-sm">
                Review these figures in ATO Online Services or with a registered tax/BAS agent before lodging.
              </Alert>
              <MonthlyGstTable rows={monthlyRows} onEditIncome={editIncome} onEditExpense={editExpense} />
              <WorksheetInputs
                incomeCount={summary.incomeSummaries.length}
                expenseCount={summary.expenses.length}
                periodLabel={summary.period.label}
                onNavigate={navigate}
              />
            </>
          ) : (
            <AnnualTaxPanel
              summary={annualSummary}
              onSaveSettings={saveAnnualSettings}
              onSaveAdjustment={saveAnnualAdjustment}
              onDeleteAdjustment={deleteAnnualAdjustment}
              onOpenIncome={editIncome}
              onOpenExpense={editExpense}
            />
          )}
        </div>
      ) : null}

      {activeView === "income" ? (
        <div className="flex flex-col gap-5">
          <MobilePanelSwitcher
            value={incomeMobilePanel}
            onChange={setIncomeMobilePanel}
            manualLabel="Manual income"
            scanLabel="Scan source"
          />
          <div className="grid gap-5 xl:grid-cols-2">
            <div className={cn(incomeMobilePanel === "scan" ? "block" : "hidden xl:block")}>
              <PdfScanPanel
                plan={userPlan}
                planSource={appData.profile.planSource}
                billingManagementAvailable={appData.profile.billingManagementAvailable}
                scanQuotaLimit={appData.profile.scanQuotaLimit}
                scanQuotaRemaining={appData.profile.scanQuotaRemaining}
                targetKind="income_summary"
                onOpenSettings={() => navigate("settings")}
                onDraftIncome={(draft) => {
                  setEditingIncome(null);
                  setIncomeDraft(draft);
                  setIncomeMobilePanel("manual");
                  scrollToForm(incomeFormRef, { focus: false });
                }}
              />
            </div>
            <div ref={incomeFormRef} className={cn(incomeMobilePanel === "manual" ? "block" : "hidden xl:block")}>
              <IncomeForm
                userId={authUser.id}
                draft={incomeDraft}
                editingIncome={editingIncome}
                onSave={saveIncome}
                onCancelEdit={() => setEditingIncome(null)}
              />
            </div>
          </div>
          <IncomeTable
            incomeSummaries={summary.incomeSummaries}
            documents={summary.documents}
            periodLabel={summary.period.label}
            onEdit={editIncome}
            onDelete={deleteIncome}
          />
        </div>
      ) : null}

      {activeView === "expenses" ? (
        <div className="flex flex-col gap-5">
          <MobilePanelSwitcher
            value={expenseMobilePanel}
            onChange={setExpenseMobilePanel}
            manualLabel="Manual expense"
            scanLabel="Scan bill"
          />
          <div className="grid gap-5 xl:grid-cols-2">
            <div className={cn(expenseMobilePanel === "scan" ? "block" : "hidden xl:block")}>
              <PdfScanPanel
                plan={userPlan}
                planSource={appData.profile.planSource}
                billingManagementAvailable={appData.profile.billingManagementAvailable}
                scanQuotaLimit={appData.profile.scanQuotaLimit}
                scanQuotaRemaining={appData.profile.scanQuotaRemaining}
                targetKind="expense_bill"
                onOpenSettings={() => navigate("settings")}
                onDraftExpense={(draft) => {
                  setEditingExpense(null);
                  setExpenseDraft(draft);
                  setExpenseMobilePanel("manual");
                  scrollToForm(expenseFormRef, { focus: false });
                }}
              />
            </div>
            <div ref={expenseFormRef} className={cn(expenseMobilePanel === "manual" ? "block" : "hidden xl:block")}>
              <ExpenseForm
                userId={authUser.id}
                profile={appData.profile}
                draft={expenseDraft}
                editingExpense={editingExpense}
                onSave={saveExpense}
                onCancelEdit={() => setEditingExpense(null)}
              />
            </div>
          </div>
          <ExpenseTable expenses={summary.expenses} documents={appData.documents} periodLabel={summary.period.label} onEdit={editExpense} onDelete={deleteExpense} />
        </div>
      ) : null}

      {activeView === "documents" ? (
        <DocumentsTable documents={appData.documents} expenses={appData.expenses} onRemovePdf={removeDocumentPdf} />
      ) : null}

      {activeView === "settings" ? (
        <SettingsPanel
          profile={appData.profile}
          onSave={saveSettings}
        />
      ) : null}
    </AppShell>
  );
}

function SettingsOnboardingPrompt({
  onOpenSettings,
  onDismiss,
}: {
  onOpenSettings: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className="mb-5 rounded-lg border border-primary/25 bg-primary/5 p-4" aria-labelledby="settings-onboarding-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 id="settings-onboarding-title" className="text-sm font-semibold text-ink">Finish your GST setup</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-muted">
            Add your ABN, BAS frequency, business-use percentage, and reminder preferences before you start building worksheets.
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button type="button" variant="secondary" onClick={onDismiss}>
            Skip for now
          </Button>
          <Button type="button" onClick={onOpenSettings}>
            Open settings
          </Button>
        </div>
      </div>
    </section>
  );
}

function WorksheetInputs({
  incomeCount,
  expenseCount,
  periodLabel,
  onNavigate,
}: {
  incomeCount: number;
  expenseCount: number;
  periodLabel: string;
  onNavigate: (view: AppView) => void;
}) {
  const ready = incomeCount > 0 && expenseCount > 0;

  return (
    <section className="rounded-lg border border-border bg-white/70 p-3 sm:p-4" aria-labelledby="worksheet-inputs-title">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 id="worksheet-inputs-title" className="text-sm font-semibold text-ink">Update records</h2>
          <p className="mt-0.5 truncate text-xs text-muted">{periodLabel} worksheet inputs</p>
        </div>
        <Badge variant={ready ? "credit" : "outline"} className="shrink-0">
          {ready ? "Ready" : "Missing data"}
        </Badge>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <WorksheetInputAction
          icon={WalletCards}
          title="Income"
          description="Monthly rideshare income feeds G1 and 1A."
          actionLabel="Add income"
          status={incomeCount > 0 ? `${incomeCount} recorded` : "No income yet"}
          complete={incomeCount > 0}
          onClick={() => onNavigate("income")}
        />
        <WorksheetInputAction
          icon={ReceiptText}
          title="Business expenses"
          description="Business purchases and deductions feed 1B."
          actionLabel="Add expense"
          status={expenseCount > 0 ? `${expenseCount} recorded` : "No expenses yet"}
          complete={expenseCount > 0}
          onClick={() => onNavigate("expenses")}
        />
      </div>
    </section>
  );
}

function DashboardModeSwitcher({
  value,
  onChange,
}: {
  value: DashboardMode;
  onChange: (value: DashboardMode) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface p-1 sm:w-fit" role="tablist" aria-label="Dashboard worksheet mode">
      <button
        type="button"
        role="tab"
        aria-selected={value === "bas"}
        className={cn(
          "min-h-11 rounded-md px-4 text-sm font-semibold text-muted transition-colors",
          value === "bas" && "bg-white text-primary shadow-sm",
        )}
        onClick={() => onChange("bas")}
      >
        BAS worksheet
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "annual_tax"}
        className={cn(
          "min-h-11 rounded-md px-4 text-sm font-semibold text-muted transition-colors",
          value === "annual_tax" && "bg-white text-primary shadow-sm",
        )}
        onClick={() => onChange("annual_tax")}
      >
        Annual tax
      </button>
    </div>
  );
}

function MobilePanelSwitcher({
  value,
  onChange,
  manualLabel,
  scanLabel,
}: {
  value: "manual" | "scan";
  onChange: (value: "manual" | "scan") => void;
  manualLabel: string;
  scanLabel: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface p-1 xl:hidden" role="tablist" aria-label="Entry mode">
      <button
        type="button"
        role="tab"
        aria-selected={value === "scan"}
        className={cn(
          "min-h-11 rounded-md px-3 text-sm font-semibold text-muted transition-colors",
          value === "scan" && "bg-white text-primary shadow-sm",
        )}
        onClick={() => onChange("scan")}
      >
        {scanLabel}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "manual"}
        className={cn(
          "min-h-11 rounded-md px-3 text-sm font-semibold text-muted transition-colors",
          value === "manual" && "bg-white text-primary shadow-sm",
        )}
        onClick={() => onChange("manual")}
      >
        {manualLabel}
      </button>
    </div>
  );
}

function WorksheetInputAction({
  icon: Icon,
  title,
  description,
  actionLabel,
  status,
  complete,
  onClick,
}: {
  icon: typeof WalletCards;
  title: string;
  description: string;
  actionLabel: string;
  status: string;
  complete: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex min-h-16 items-center justify-between gap-3 rounded-lg border border-border bg-surface/70 px-3 py-2 text-left transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      aria-label={`${actionLabel}. ${status}`}
      onClick={onClick}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white text-primary ring-1 ring-border">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-ink">{title}</span>
          <span className="mt-0.5 hidden text-xs leading-5 text-muted sm:block">{description}</span>
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Badge variant={complete ? "credit" : "outline"}>
          {status}
        </Badge>
        <ArrowRight className="size-4" aria-hidden="true" />
      </span>
    </button>
  );
}
